/**
 * Which stored files may be RENDERED in a browser tab — and as what.
 *
 * Every authenticated file in this app reaches the browser as a `blob:` URL, and a blob URL
 * runs in the origin of the page that made it: OURS. A tab pointed at an HTML or SVG blob
 * executes its script with the app's storage — including the device token — in reach.
 * Inbound email attachments come from anyone, so "open the attachment" must never be able
 * to do that.
 *
 * Two rules, both needed:
 *  - Only these extensions open in a tab. Everything else downloads: a `download` anchor
 *    saves the bytes and never renders them, so it is safe for any type. SVG is NOT here —
 *    it is an image that can carry script.
 *  - The blob is re-typed from the EXTENSION, never the response header. The stored
 *    `mime_type` comes from the sender, so a `.txt` declared `text/html` would otherwise
 *    render as a page.
 */
import { triggerBlobDownload } from '@/utils/file-download';

const SAFE_TAB_TYPES: Record<string, string> = {
	pdf: 'application/pdf',
	png: 'image/png',
	jpg: 'image/jpeg',
	jpeg: 'image/jpeg',
	gif: 'image/gif',
	webp: 'image/webp',
	txt: 'text/plain',
};

const extensionOf = (fileName: string): string => (fileName.includes('.') ? fileName.split('.').pop() ?? '' : '').toLowerCase();

/** The forced type a file may be opened in a tab as, or null when it must be downloaded. */
export const safeTabType = (fileName: string): string | null => SAFE_TAB_TYPES[extensionOf(fileName)] ?? null;

/** A blob URL safe to navigate a tab to, typed from the extension — or null when it is not. */
export async function safeTabBlobUrl(response: Response, fileName: string): Promise<string | null> {
	const type = safeTabType(fileName);
	if (!type) return null;
	return URL.createObjectURL(new Blob([await response.arrayBuffer()], { type }));
}

/**
 * Save a response to disk without rendering it — safe for every type. Web only.
 *
 * Through `triggerBlobDownload`, which attaches the anchor and revokes LATER: revoking
 * synchronously after `click()` can cancel a download a browser has not started yet.
 */
export async function saveResponseAs(response: Response, fileName: string): Promise<void> {
	triggerBlobDownload(await response.blob(), fileName);
}
